import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { AuthUserResponseDto } from '@lunary/api-client'
import { api, ApiError, setAccessToken } from '../api/client'

type Status = 'loading' | 'guest' | 'forbidden' | 'unavailable' | 'authenticated'

type AdminAuthValue = {
  user: AuthUserResponseDto | null
  status: Status
  logout: () => Promise<void>
  retry: () => void
}

const AdminAuthContext = createContext<AdminAuthValue | null>(null)

function isExpert(role: string | undefined): boolean {
  return role === 'expert' || role === 'admin'
}

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUserResponseDto | null>(null)
  const [status, setStatus] = useState<Status>('loading')

  const restore = useCallback(async () => {
    try {
      const res = await api.post<{ access_token: string; user: AuthUserResponseDto }>('/auth/refresh')
      setAccessToken(res.access_token)
      setUser(res.user)
      setStatus(isExpert(res.user.role) ? 'authenticated' : 'forbidden')
    } catch (e) {
      setStatus(e instanceof ApiError && e.status < 500 ? 'forbidden' : 'unavailable')
    }
  }, [])

  const retry = useCallback(() => {
    setStatus('loading')
    void restore()
  }, [restore])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      await restore()
      if (cancelled) return
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [restore])

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined)
    } catch {
      // refresh cookie может быть невалиден — просто сбрасываем
    }
    setAccessToken(null)
    setUser(null)
    setStatus('forbidden')
  }, [])

  const value = useMemo<AdminAuthValue>(
    () => ({ user, status, logout, retry }),
    [user, status, logout, retry],
  )

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
}

export function useAdminAuth(): AdminAuthValue {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider')
  return ctx
}
